// CSS capture for the report target. Two layers:
//   computed_styles — the resolved truth (a curated, bug-relevant subset of
//                     getComputedStyle; the full set is ~350 props and noise).
//   matched_rules   — the *authored* rules that apply, with their selector and
//                     source stylesheet, so an agent can jump to the CSS in code.
// matched_rules is best-effort: cross-origin stylesheets throw on .cssRules and
// are skipped (noted via a flag) — capture never blocks a report.

// Curated set: colour, type, box model, layout, effects. Enough to reason about
// a visual defect; small enough to keep the report light.
const TRACKED = [
  'color', 'background-color', 'background-image', 'opacity', 'visibility',
  'font-family', 'font-size', 'font-weight', 'font-style', 'line-height',
  'letter-spacing', 'text-align', 'text-transform', 'text-decoration', 'white-space',
  'display', 'position', 'top', 'right', 'bottom', 'left', 'z-index', 'float', 'overflow',
  'width', 'height', 'min-width', 'max-width', 'min-height', 'max-height', 'box-sizing',
  'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'border-top-width', 'border-style', 'border-color', 'border-radius',
  'box-shadow', 'outline',
  'flex-direction', 'flex-wrap', 'justify-content', 'align-items', 'gap',
  'grid-template-columns', 'grid-template-rows',
  'cursor', 'transform', 'transition',
]

const MAX_RULES = 24
const MAX_DECL_LEN = 800

export interface MatchedRule {
  selector: string
  source: string // stylesheet href, or 'inline' / 'style-attr'
  media?: string // enclosing @media condition, if any
  declarations: string
}

/** Curated computed styles as prop→value (only values that differ from initial noise are kept). */
export function computedStyles(el: Element): Record<string, string> {
  const cs = getComputedStyle(el)
  const out: Record<string, string> = {}
  for (const prop of TRACKED) {
    const v = cs.getPropertyValue(prop)
    if (v && v !== 'normal' && v !== 'auto' && v !== 'none' && v !== '0px') out[prop] = v.trim()
  }
  return out
}

/** Collect matching CSSStyleRules from a rule list into `out`. */
function collectFrom(el: Element, cssRules: CSSRuleList, source: string, out: MatchedRule[], media?: string): void {
  for (const rule of Array.from(cssRules)) {
    if (out.length >= MAX_RULES) break
    if (rule instanceof CSSStyleRule) {
      if (rule.selectorText && safeMatches(el, rule.selectorText)) {
        out.push({
          selector: rule.selectorText,
          source,
          ...(media ? { media } : {}),
          declarations: (rule.style?.cssText ?? '').slice(0, MAX_DECL_LEN),
        })
      }
    } else if (typeof CSSMediaRule !== 'undefined' && rule instanceof CSSMediaRule) {
      collectFrom(el, rule.cssRules, source, out, rule.conditionText)
    }
  }
}

/**
 * Authored rules that match the element, from same-origin / CORS-readable
 * stylesheets. `unreadable` lists the hrefs of cross-origin sheets whose CSSOM
 * threw — those can be recovered asynchronously via {@link recoverRules}.
 */
export function matchedRules(el: Element): { rules: MatchedRule[]; unreadable: string[] } {
  const rules: MatchedRule[] = []
  const unreadable: string[] = []

  for (const sheet of Array.from(document.styleSheets)) {
    let cssRules: CSSRuleList
    try {
      cssRules = sheet.cssRules // throws SecurityError for cross-origin sheets
    } catch {
      if (sheet.href) unreadable.push(sheet.href)
      continue
    }
    collectFrom(el, cssRules, sheetHref(sheet), rules)
  }

  const inline = (el as HTMLElement).getAttribute?.('style')
  if (inline && rules.length < MAX_RULES) {
    rules.push({ selector: '(inline style attribute)', source: 'style-attr', declarations: inline.slice(0, MAX_DECL_LEN) })
  }

  return { rules, unreadable }
}

/**
 * Recover matched rules from cross-origin stylesheets by *fetching* them (works
 * when the CDN sends permissive CORS headers — many do). Not a cache bypass:
 * a fetch the server refuses to make CORS-readable still fails, and we skip it.
 * Parses fetched CSS in a detached constructable stylesheet (CSS is data, never
 * executed). Returns recovered rules + the hrefs still unreadable.
 */
export async function recoverRules(
  el: Element,
  hrefs: string[],
  budget = MAX_RULES,
): Promise<{ rules: MatchedRule[]; stillUnreadable: string[] }> {
  const rules: MatchedRule[] = []
  const stillUnreadable: string[] = []
  if (typeof CSSStyleSheet === 'undefined' || !('replaceSync' in CSSStyleSheet.prototype)) {
    return { rules, stillUnreadable: hrefs }
  }
  for (const href of hrefs) {
    if (rules.length >= budget) {
      stillUnreadable.push(href)
      continue
    }
    try {
      const res = await fetch(href, { cache: 'force-cache' }) // prefer the copy already fetched
      if (!res.ok) throw new Error(String(res.status))
      const text = await res.text()
      const sheet = new CSSStyleSheet()
      sheet.replaceSync(text)
      const before = rules.length
      collectFrom(el, sheet.cssRules, sheetPath(href) + ' (fetched)', rules)
      if (rules.length === before) {
        // readable but no matching rule — that's fine, not "unreadable"
      }
    } catch {
      stillUnreadable.push(href) // CORS-blocked, offline, or CSP connect-src — degrade
    }
  }
  return { rules, stillUnreadable }
}

function sheetPath(href: string): string {
  try {
    return new URL(href).pathname
  } catch {
    return href
  }
}

function safeMatches(el: Element, selector: string): boolean {
  try {
    return el.matches(selector)
  } catch {
    // Selectors with ::before / :hover etc. throw in matches(); treat as non-matching.
    return false
  }
}

function sheetHref(sheet: CSSStyleSheet | null): string {
  if (!sheet) return 'unknown'
  if (sheet.href) {
    try {
      return new URL(sheet.href).pathname
    } catch {
      return sheet.href
    }
  }
  return 'inline <style>'
}
